import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { KeyRound, Loader2, Phone, BadgeCheck, History, ShieldCheck, Laptop, Trash2, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { celebrateFrom } from "@/lib/celebrate";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/nav";
import PageHeader, { schoolYear } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { useFeatureFlags } from "@/lib/featureFlags";
import { CoachAdminInvites, CoachRoleRequests } from "@/pages/coach/CoachRoleControls";
import CollapsibleCard from "@/components/CollapsibleCard";
import MemberBioCard from "@/components/bios/MemberBioCard";
import MemberLegalCards from "@/components/profile/MemberLegalCards";
import TheatreFeatureToggles from "@/components/profile/TheatreFeatureToggles";
import ScheduleTabs, { useTabParam } from "@/components/ScheduleTabs";

const MEMBER_TABS = [{ key: "account", label: "Account" }, { key: "settings", label: "Settings" }, { key: "legal", label: "Legal" }];

const COACH_TITLES = ["Coach", "Mr.", "Ms.", "Dr.", "Director"];

const maskLastName = (n) => { n = (n || "").trim(); return n ? `${n[0]}.` : ""; };
const maskEmail = (e) => {
  e = (e || "").trim();
  if (!e || !e.includes("@")) return e;
  const [local, domain] = e.split("@");
  const masked = local.slice(0, 2) + "x".repeat(Math.max(local.length - 2, 3));
  return `${masked}@${domain}`;
};
const maskPhone = (p) => {
  const digits = (p || "").replace(/\D/g, "");
  return digits ? `xxx-xxx-xx${digits.slice(-2)}` : "";
};

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-800">{value || "—"}</span>
    </div>
  );
}

function DataDeletionCard() {
  const { district_admin_account: daOn, campus_admin_account: caOn } = useFeatureFlags();
  const [request, setRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const load = React.useCallback(() => api.get("/deletion-requests/mine").then(({ data }) => setRequest(data.request)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/deletion-requests", { reason: reason || undefined }); toast.success("Your data deletion request has been submitted."); setOpen(false); setReason(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { timeZone: "America/Chicago", year: "numeric", month: "short", day: "numeric" }) : "";
  const status = request?.status;
  const denied = status === "denied";
  const closedNoDeletion = status === "completed" && !!request?.deactivated;
  const approved = (status === "approved" || status === "completed") && !closedNoDeletion;
  const pending = !!request && !denied && !approved && !closedNoDeletion;
  const canRequest = !request || denied || closedNoDeletion;
  if (!daOn && !caOn) return null;
  return (
    <CollapsibleCard title="Delete my data" testId="data-deletion-card" className="border-red-200">
      <p className="mb-4 mt-1 text-sm text-slate-500">Request permanent deletion of your account and all of your records. A District Administrator will review your request. This cannot be undone.</p>
      {request && (
        <div className="mb-4" data-testid="deletion-status-row">
          <span className="mr-2 text-sm font-medium text-slate-500">Request status:</span>
          {pending && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-700" data-testid="deletion-status-pill">
              <span className="h-2 w-2 rounded-full bg-amber-500" /> Pending review · submitted {fmtDate(request.created_at)}
            </span>
          )}
          {denied && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-700" data-testid="deletion-status-pill">
              <span className="h-2 w-2 rounded-full bg-red-500" /> Denied · {fmtDate(request.handled_at)}
            </span>
          )}
          {approved && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-700" data-testid="deletion-status-pill">
              <span className="h-2 w-2 rounded-full bg-emerald-500" /> Approved · {fmtDate(request.handled_at)}
            </span>
          )}
          {closedNoDeletion && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-600" data-testid="deletion-status-pill">
              <span className="h-2 w-2 rounded-full bg-slate-400" /> Closed — no deletion · {fmtDate(request.completed_at || request.handled_at)}
            </span>
          )}
        </div>
      )}
      {closedNoDeletion && <p className="mb-3 text-sm text-slate-500">Your request was closed without deletion. You may submit a new one if needed.</p>}
      {denied && <p className="mb-3 text-sm text-slate-500">Your previous request was declined. You may submit a new one.</p>}
      {canRequest && (
        <Button variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50" onClick={() => setOpen(true)} data-testid="request-deletion-btn">Request deletion of my data</Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="request-deletion-dialog">
          <DialogHeader><DialogTitle className="font-heading text-red-600">Request data deletion</DialogTitle>
            <DialogDescription>Your account and all records will be permanently deleted once an administrator approves. This is irreversible.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Let us know why (optional)" data-testid="deletion-reason-input" />
          </div>
          <DialogFooter><Button className="rounded-full bg-red-600 hover:bg-red-700" disabled={busy} onClick={submit} data-testid="submit-deletion-request">Submit request</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CollapsibleCard>
  );
}

function TrustedDevicesCard() {
  const DEVICE_KEY = "rallypointgo_device";
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/auth/trusted-devices", { headers: { "X-Device-Token": localStorage.getItem(DEVICE_KEY) || "" } })
      .then(({ data }) => setDevices(data.devices || []))
      .catch((e) => { setDevices([]); toast.error(formatApiError(e.response?.data?.detail) || "Could not load trusted devices."); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const fmt = (d) => d ? new Date(d).toLocaleString("en-US", { timeZone: "America/Chicago", year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

  const removeOne = async (device) => {
    setBusyId(device.id);
    try {
      await api.delete(`/auth/trusted-devices/${device.id}`);
      if (device.is_current) localStorage.removeItem(DEVICE_KEY);
      toast.success("Trusted device removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const removeAll = async () => {
    setClearing(true);
    try {
      const { data } = await api.delete("/auth/trusted-devices");
      localStorage.removeItem(DEVICE_KEY);
      toast.success(`Removed ${data.removed || 0} trusted device${(data.removed || 0) === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setClearing(false); }
  };

  return (
    <CollapsibleCard title="Trusted devices" icon={Laptop} testId="trusted-devices-card">
      <p className="mb-4 text-sm text-slate-500">
        Devices where you chose “remember this device” skip two-factor verification for 30 days. Remove any you don't recognize.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-slate-400" data-testid="trusted-devices-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading devices…
        </div>
      ) : devices.length === 0 ? (
        <p className="py-2 text-sm text-slate-400" data-testid="trusted-devices-empty">No trusted devices. You'll be asked for two-factor verification each time you sign in.</p>
      ) : (
        <>
          <ul className="space-y-3" data-testid="trusted-devices-list">
            {devices.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`trusted-device-${d.id}`}>
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <span className="truncate">{d.name}</span>
                    {d.is_current && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`trusted-device-current-${d.id}`}>
                        <CheckCircle2 className="h-3 w-3" /> This device
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-400">Last login: {fmt(d.last_used_at)}</p>
                </div>
                <Button variant="outline" size="sm" disabled={busyId === d.id}
                  className="shrink-0 rounded-full border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => removeOne(d)} data-testid={`remove-trusted-device-${d.id}`}>
                  {busyId === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  <span className="ml-1">Remove</span>
                </Button>
              </li>
            ))}
          </ul>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="mt-4 rounded-full border-red-300 text-red-600 hover:bg-red-50" disabled={clearing} data-testid="remove-all-trusted-devices">
                {clearing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Remove all devices
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="remove-all-trusted-devices-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Remove all trusted devices?</AlertDialogTitle>
                <AlertDialogDescription>
                  Every device will be signed out of the trusted list. You'll need to complete two-factor verification the next time you sign in on any device.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="remove-all-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={removeAll} data-testid="remove-all-confirm">Remove all</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </CollapsibleCard>
  );
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [ref, setRef] = useState({ districts: [], campuses: [], teams: [] });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contact, setContact] = useState({ member_phone: "", guardian_first_name: "", guardian_last_name: "", guardian_phone: "", guardian_email: "" });
  const [savingContact, setSavingContact] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [myGroups, setMyGroups] = useState([]);
  const [accountChanges, setAccountChanges] = useState([]);
  const [title, setTitle] = useState(user.title || "Coach");
  const [savingTitle, setSavingTitle] = useState(false);
  const [pinStatus, setPinStatus] = useState(null);
  const [pinForm, setPinForm] = useState({ current_pin: "", new_pin: "", confirm_pin: "" });
  const [savingPin, setSavingPin] = useState(false);
  const [pinOtp, setPinOtp] = useState({ sent: false, challenge_id: "", code: "", email: "", sending: false });
  const [tab, setTab] = useTabParam(MEMBER_TABS.map((t) => t.key));
  const { search } = useLocation();
  const section = new URLSearchParams(search).get("section");
  const openContact = section === "contact";
  const openOversight = section === "oversight";
  useEffect(() => {
    const id = { contact: "member-contact-card", oversight: "coach-oversight-card" }[section];
    if (id) document.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [section]);

  const loadPinStatus = useCallback(() => {
    if (user.role === "coach") api.get("/coach/phonebook/status").then(({ data }) => setPinStatus(data)).catch(() => {});
  }, [user.role]);
  const [oversight, setOversight] = React.useState(null);
  React.useEffect(() => {
    if (user.role === "coach") api.get("/coach/oversight").then(({ data }) => setOversight(data)).catch(() => {});
  }, [user.role]);
  useEffect(() => { loadPinStatus(); }, [loadPinStatus]);

  const validatePinForm = () => {
    if (pinStatus?.pin_set && !pinForm.current_pin) { toast.error("Enter your current PIN"); return false; }
    if (!(pinForm.new_pin.length === 4 || pinForm.new_pin.length === 6)) { toast.error("PIN must be 4 or 6 digits"); return false; }
    if (pinForm.new_pin !== pinForm.confirm_pin) { toast.error("PINs do not match"); return false; }
    return true;
  };

  const requestPinOtp = async () => {
    if (!validatePinForm()) return;
    setPinOtp((o) => ({ ...o, sending: true }));
    try {
      const { data } = await api.post("/coach/phonebook/pin/request-otp");
      setPinOtp({ sent: true, challenge_id: data.challenge_id, code: "", email: data.email, sending: false });
      toast.success(`Verification code sent to ${data.email}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setPinOtp((o) => ({ ...o, sending: false })); }
  };

  const savePin = async (e) => {
    e.preventDefault();
    if (!validatePinForm()) return;
    if (!/^\d{6}$/.test(pinOtp.code)) { toast.error("Enter the 6-digit code sent to your email"); return; }
    setSavingPin(true);
    try {
      await api.post("/coach/phonebook/pin", {
        new_pin: pinForm.new_pin, current_pin: pinForm.current_pin || undefined,
        challenge_id: pinOtp.challenge_id, otp: pinOtp.code,
      });
      toast.success("Phonebook PIN saved");
      setPinForm({ current_pin: "", new_pin: "", confirm_pin: "" });
      setPinOtp({ sent: false, challenge_id: "", code: "", email: "", sending: false });
      loadPinStatus();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingPin(false); }
  };

  useEffect(() => { api.get("/reference").then(({ data }) => setRef(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user.role === "team_member") api.get("/member/subgroups").then(({ data }) => setMyGroups(data.items || [])).catch(() => {});
  }, [user.role]);
  const loadChanges = useCallback(() => {
    if (user.role === "team_member") api.get("/member/account-changes").then(({ data }) => setAccountChanges(data.items || [])).catch(() => {});
  }, [user.role]);
  useEffect(() => { loadChanges(); }, [loadChanges]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (editingContact) return; // never overwrite the blank edit form with saved values
    setContact({
      member_phone: user.member_phone || "",
      guardian_first_name: user.guardian_first_name || "",
      guardian_last_name: user.guardian_last_name || "",
      guardian_phone: user.guardian_phone || "",
      guardian_email: user.guardian_email || "",
    });
  }, [editingContact, user.member_phone, user.guardian_first_name, user.guardian_last_name, user.guardian_phone, user.guardian_email]);

  useEffect(() => { setTitle(user.title || "Coach"); }, [user.title]);

  const saveTitle = async (newTitle) => {
    setTitle(newTitle);
    setSavingTitle(true);
    try {
      await api.put("/coach/profile", { title: newTitle });
      toast.success("Title updated");
      await refresh();
    } catch (err) {
      setTitle(user.title || "Coach");
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSavingTitle(false); }
  };

  const startEditContact = () => {
    // Blank inputs so real values are never re-exposed; guardian last name stays visible.
    setContact({ member_phone: "", guardian_first_name: "", guardian_last_name: user.guardian_last_name || "", guardian_phone: "", guardian_email: "" });
    setEditingContact(true);
  };

  // "Complete now" from the dashboard lands here with ?section=contact: open the edit form right away.
  useEffect(() => {
    if (openContact && user.role === "team_member") {
      setContact({ member_phone: "", guardian_first_name: "", guardian_last_name: user.guardian_last_name || "", guardian_phone: "", guardian_email: "" });
      setEditingContact(true);
    }
  }, [openContact]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveContact = async (e) => {
    e.preventDefault();
    const payload = {};
    Object.entries(contact).forEach(([k, v]) => { if ((v || "").trim() !== "") payload[k] = v.trim(); });
    if (Object.keys(payload).length === 0) { toast.error("Enter at least one field to update"); return; }
    setSavingContact(true);
    try {
      const { data: res } = await api.put("/member/profile", payload);
      toast.success("Contact info updated");
      celebrateFrom(res);
      setEditingContact(false);
      await refresh();
      loadChanges();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSavingContact(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed successfully");
      setPw({ current_password: "", new_password: "", confirm: "" });
      loadChanges();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const district = user.is_staff ? null : ref.districts.find((d) => d.id === user.district_id);
  const campus = ref.campuses.find((c) => c.id === user.campus_id);
  const team = ref.teams.find((t) => t.id === user.team_id);
  const initials = `${(user.first_name || "")[0] || ""}${(user.last_name || "")[0] || ""}`.toUpperCase();

  // Data masking: team members and parents see a masked version of their own name/email.
  const isMember = user.role === "team_member";
  const isParent = user.role === "parent";
  const displayFirst = isParent ? "xxxxx" : user.first_name;
  const displayLast = isMember ? maskLastName(user.last_name) : user.last_name;
  const displayEmail = (isMember || isParent) ? maskEmail(user.email) : user.email;
  const displayInitials = isParent ? `${(user.last_name || "")[0] || ""}`.toUpperCase() : initials;

  const show = (key) => tab === key;
  const titleNode = isMember ? (
    <span className="flex flex-wrap items-center gap-3">
      Profile
      {myGroups.map((g) => (
        <span key={g.id} className="rounded-full bg-violet-100 px-3 py-1 font-sans text-sm font-medium tracking-normal text-violet-700" data-testid={`my-group-${g.id}`}>{g.name}</span>
      ))}
    </span>
  ) : "Profile";

  return (
    <div>
      <PageHeader title={titleNode} subtitle="Your account details" />
      <ScheduleTabs tabs={MEMBER_TABS} value={tab} onChange={setTab} testId="profile" />
      <div className="[&>*:first-child]:mt-0" data-testid={`profile-panel-${tab}`}>
      {show("account") && (
      <Card className="max-w-2xl p-6 border-slate-200 shadow-sm" data-testid="profile-card">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-sky-100 text-2xl font-extrabold text-sky-700 font-heading">
            {displayInitials || "U"}
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900 font-heading" data-testid="profile-name">{displayFirst} {displayLast}</p>
            <p className="text-sm text-slate-500" data-testid="profile-email">{displayEmail}</p>
            <span className="mt-1 inline-flex rounded-full bg-slate-900 px-2.5 py-0.5 text-xs font-semibold text-white">{ROLE_LABEL[user.role]}</span>
          </div>
        </div>
        <Separator className="my-5" />
        <div className="divide-y divide-slate-100">
          <Row label="District" value={user.is_staff ? "Staff" : district?.name} />
          <Row label="Campus" value={campus ? `${campus.name} (${campus.code})` : null} />
          <Row label="Team" value={team ? `${team.name} (${team.code})` : null} />
          <Row label="Active Year" value={schoolYear(user.active_year)} />
          {user.role === "team_member" && (
            <Row label="Graduation Year" value={user.graduation_year} />
          )}
          <Row label="Assignment Status" value={user.assignment_status} />
        </div>
      </Card>
      )}

      {user.role === "coach" && show("settings") && (
        <CollapsibleCard title="Display title" icon={BadgeCheck} testId="coach-title-card">
          <p className="mb-4 text-sm text-slate-500">
            Choose how your name appears to members, parents and admins across notifications, announcements, emails and reports.
          </p>
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-40 space-y-2">
              <Label>Title</Label>
              <Select value={title} onValueChange={saveTitle} disabled={savingTitle}>
                <SelectTrigger data-testid="coach-title-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COACH_TITLES.map((t) => <SelectItem key={t} value={t} data-testid={`coach-title-option-${t}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="pb-2 text-sm text-slate-500">
              Displayed as <span className="font-bold text-slate-800" data-testid="coach-display-name-preview">{title} {user.last_name}</span>
              {savingTitle && <Loader2 className="ml-2 inline h-4 w-4 animate-spin text-slate-400" />}
            </div>
          </div>
        </CollapsibleCard>
      )}

      {user.role === "coach" && show("settings") && <TheatreFeatureToggles />}

      {user.role === "coach" && show("settings") && (
        <CollapsibleCard title="Oversight" icon={ShieldCheck} testId="coach-oversight-card" defaultOpen={openOversight}>
          <p className="mb-4 text-sm text-slate-500">Administrators with view-only oversight of your team account.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div data-testid="oversight-district">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">District Administrator</p>
              {oversight?.district_admins?.length ? oversight.district_admins.map((a, i) => (
                <p key={i} className="mt-1 text-sm font-semibold text-slate-800">{a.name} <span className="font-normal text-slate-400">· {a.email}</span></p>
              )) : oversight?.listed_district_admin?.email ? (
                <p className="mt-1 text-sm text-amber-600" data-testid="oversight-district-pending">{oversight.listed_district_admin.name || oversight.listed_district_admin.email} · invite pending</p>
              ) : <p className="mt-1 text-sm text-slate-400">None assigned</p>}
            </div>
            <div data-testid="oversight-campus">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Campus Administrator</p>
              {oversight?.campus_admins?.length ? oversight.campus_admins.map((a, i) => (
                <p key={i} className="mt-1 text-sm font-semibold text-slate-800">{a.name} <span className="font-normal text-slate-400">· {a.email}</span></p>
              )) : oversight?.listed_campus_admin?.email ? (
                <p className="mt-1 text-sm text-amber-600" data-testid="oversight-campus-pending">{oversight.listed_campus_admin.name || oversight.listed_campus_admin.email} · invite pending</p>
              ) : <p className="mt-1 text-sm text-slate-400">None assigned</p>}
            </div>
          </div>
          <div className="mt-6 border-t border-slate-100 pt-6">
            <CoachAdminInvites />
          </div>
          <div className="mt-6 border-t border-slate-100 pt-6">
            <CoachRoleRequests />
          </div>
        </CollapsibleCard>
      )}

      {user.role === "coach" && show("settings") && (
        <CollapsibleCard title="Phonebook PIN" icon={KeyRound} testId="phonebook-pin-card">
          <p className="mb-4 text-sm text-slate-500">
            Your phonebook is protected by a personal 4 or 6-digit PIN. {pinStatus?.pin_set ? "You have a PIN set — enter your current PIN to change it." : "Set a PIN to unlock your phonebook."}
            {pinStatus?.locked && <span className="mt-1 block font-semibold text-red-600">Your phonebook is currently locked. Ask an administrator to unlock it.</span>}
          </p>
          <form onSubmit={savePin} className="space-y-4" data-testid="phonebook-pin-form">
            {pinStatus?.pin_set && (
              <div className="space-y-2 max-w-xs">
                <Label>Current PIN</Label>
                <Input type="password" inputMode="numeric" maxLength={6} value={pinForm.current_pin}
                  onChange={(e) => setPinForm((f) => ({ ...f, current_pin: e.target.value.replace(/\D/g, "") }))}
                  data-testid="phonebook-current-pin-input" />
              </div>
            )}
            <div className="grid max-w-md grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>New PIN (4 or 6 digits)</Label>
                <Input type="password" inputMode="numeric" maxLength={6} value={pinForm.new_pin}
                  onChange={(e) => setPinForm((f) => ({ ...f, new_pin: e.target.value.replace(/\D/g, "") }))}
                  data-testid="phonebook-new-pin-input" />
              </div>
              <div className="space-y-2">
                <Label>Confirm PIN</Label>
                <Input type="password" inputMode="numeric" maxLength={6} value={pinForm.confirm_pin}
                  onChange={(e) => setPinForm((f) => ({ ...f, confirm_pin: e.target.value.replace(/\D/g, "") }))}
                  data-testid="phonebook-confirm-pin-input" />
              </div>
            </div>
            <Button type="button" onClick={requestPinOtp} disabled={pinOtp.sending || savingPin} variant="outline" className="rounded-full" data-testid="phonebook-request-otp">
              {pinOtp.sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {pinOtp.sent ? "Resend email code" : "Send verification code to my email"}
            </Button>
            {pinOtp.sent && (
              <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="phonebook-otp-section">
                <p className="text-sm text-slate-600">We emailed a 6-digit verification code to <span className="font-semibold">{pinOtp.email}</span>. Enter it below to confirm your PIN change.</p>
                <div className="space-y-2 max-w-xs">
                  <Label>Email verification code</Label>
                  <Input inputMode="numeric" maxLength={6} value={pinOtp.code}
                    onChange={(e) => setPinOtp((o) => ({ ...o, code: e.target.value.replace(/\D/g, "") }))}
                    placeholder="6-digit code" data-testid="phonebook-otp-input" />
                </div>
                <Button type="submit" disabled={savingPin} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="phonebook-pin-save">
                  {savingPin && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {pinStatus?.pin_set ? "Verify & change PIN" : "Verify & set PIN"}
                </Button>
              </div>
            )}
          </form>
        </CollapsibleCard>
      )}

      {isMember && show("account") && (
        <CollapsibleCard title="Contact & emergency info" icon={Phone} testId="member-contact-card" defaultOpen={openContact}>
          <p className="mb-4 text-sm text-slate-500">Keep your phone number and a parent/guardian emergency contact up to date. For your privacy, saved details are masked.</p>
          {!editingContact ? (
            <div>
              <div className="divide-y divide-slate-100" data-testid="member-contact-readonly">
                <Row label="Member phone" value={maskPhone(user.member_phone)} />
                <Row label="Parent/Guardian name" value={[user.guardian_first_name ? "xxxxx" : "", user.guardian_last_name].filter(Boolean).join(" ")} />
                <Row label="Parent/Guardian phone (emergency)" value={maskPhone(user.guardian_phone)} />
                <Row label="Parent/Guardian email" value={maskEmail(user.guardian_email)} />
              </div>
              <Button type="button" onClick={startEditContact} className="mt-4 rounded-full bg-slate-900 hover:bg-slate-800" data-testid="member-contact-edit">
                Edit contact info
              </Button>
            </div>
          ) : (
          <form onSubmit={saveContact} className="space-y-4" data-testid="member-contact-form">
            <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">Enter new values to update. Leave a field blank to keep it unchanged.</p>
            <div className="space-y-2">
              <Label>Member phone number</Label>
              <Input value={contact.member_phone} onChange={(e) => setContact((c) => ({ ...c, member_phone: e.target.value }))}
                placeholder="e.g. 210-555-0100" autoFocus={openContact} data-testid="member-phone-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Parent/Guardian first name</Label>
                <Input value={contact.guardian_first_name} onChange={(e) => setContact((c) => ({ ...c, guardian_first_name: e.target.value }))} data-testid="guardian-first-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Parent/Guardian last name</Label>
                <Input value={contact.guardian_last_name} onChange={(e) => setContact((c) => ({ ...c, guardian_last_name: e.target.value }))} data-testid="guardian-last-name-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Parent/Guardian phone (emergency)</Label>
              <Input value={contact.guardian_phone} onChange={(e) => setContact((c) => ({ ...c, guardian_phone: e.target.value }))}
                placeholder="e.g. 210-555-0199" data-testid="guardian-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>Parent/Guardian email</Label>
              <Input type="email" value={contact.guardian_email} onChange={(e) => setContact((c) => ({ ...c, guardian_email: e.target.value }))}
                placeholder="guardian@example.com" data-testid="guardian-email-input" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={savingContact} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="member-contact-submit">
                {savingContact && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save contact info
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditingContact(false)} className="rounded-full" data-testid="member-contact-cancel">Cancel</Button>
            </div>
          </form>
          )}
        </CollapsibleCard>
      )}

      {isMember && show("account") && <MemberBioCard />}

      {show("settings") && (
      <CollapsibleCard title="Change password" icon={KeyRound} testId="change-password-card" defaultOpen={isMember || isParent}>
        <form onSubmit={changePassword} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-2">
            <Label>Current password</Label>
            <Input type={showPw ? "text" : "password"} required autoComplete="current-password" value={pw.current_password}
              onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
              data-testid="current-password-input" />
          </div>
          <div className="space-y-2">
            <Label>New password</Label>
            <Input type={showPw ? "text" : "password"} required minLength={6} autoComplete="new-password" value={pw.new_password}
              onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
              data-testid="new-password-input" />
          </div>
          <div className="space-y-2">
            <Label>Confirm new password</Label>
            <Input type={showPw ? "text" : "password"} required minLength={6} autoComplete="new-password" value={pw.confirm}
              onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
              data-testid="confirm-password-input" />
          </div>
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => setShowPw((s) => !s)}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700" data-testid="toggle-password-visibility">
              {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {showPw ? "Hide" : "Show"} passwords
            </button>
            <Button type="submit" disabled={saving || !pw.current_password || !pw.new_password} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="change-password-submit">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password
            </Button>
          </div>
        </form>
      </CollapsibleCard>
      )}

      {(isMember || isParent) && show("settings") && <DataDeletionCard />}

      {isMember && show("settings") && (
        <CollapsibleCard title="Recent account changes" icon={History} testId="account-changes-card">
          <p className="mb-4 text-sm text-slate-500">A log of changes made to your profile and password. Contact your coach or administrator if you don't recognize one.</p>
          {accountChanges.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="account-changes-empty">No account changes recorded yet.</p>
          ) : (
            <ul className="space-y-3" data-testid="account-changes-list">
              {accountChanges.map((c, i) => {
                const what = (c.message || "").split(". If you did not")[0];
                const when = c.created_at ? new Date(c.created_at).toLocaleString("en-US", { timeZone: "America/Chicago", year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
                return (
                  <li key={i} className="flex gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`account-change-${i}`}>
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sky-500" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">{what}</p>
                      <p className="mt-0.5 text-xs text-slate-400">{when}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CollapsibleCard>
      )}

      {user.role === "administrator" && !user.impersonated_by && show("settings") && (
        <TrustedDevicesCard />
      )}

      {show("legal") && <MemberLegalCards termsAcceptedAt={user.terms_accepted_at} />}
      </div>
    </div>
  );
}
